"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useSnackbar } from "notistack";
import { useTransition } from "react";
import type { Locale } from "@rmh/defs";
import { fetchGraphQLClient } from "@rmh/data-ops/src/amplify/fetchGraphQLClient";
import { usePathname, useRouter } from "@rmh/i18n";
import { usePatientUserAttributes } from "@rmh/ui/cognito";
import {
  UPDATE_PREFERRED_LANGUAGE_MUTATION_KEY,
  preferredLanguageQueryKey,
  updatePreferredLanguageMutation,
} from "./preferredLanguage";

interface PreferredLanguageSwitch {
  switchLocale: (next: Locale) => Promise<void>;
  isPending: boolean;
  disabled: boolean;
}

export const usePreferredLanguageSwitch = (): PreferredLanguageSwitch => {
  const t = useTranslations("common");
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const {
    sub,
    isImpersonating,
    isPending: attributesPending,
  } = usePatientUserAttributes();
  const [isNavigating, startTransition] = useTransition();

  const { mutateAsync, isPending: isSaving } = useMutation({
    mutationKey: UPDATE_PREFERRED_LANGUAGE_MUTATION_KEY,
    mutationFn: async (preferredLanguage: Locale) => {
      const response = await fetchGraphQLClient(
        updatePreferredLanguageMutation,
        { input: { id: sub as string, preferredLanguage } }
      );
      if (response.errors?.length) throw response.errors;
    },
  });

  const navigate = (locale: Locale): void =>
    startTransition(() => {
      router.replace(`${pathname}${window.location.search}`, { locale });
    });

  const switchLocale = async (next: Locale): Promise<void> => {
    if (isImpersonating) return;
    if (!sub) return navigate(next);

    try {
      await mutateAsync(next);
    } catch (error) {
      console.error("Failed to save preferred language", error);
      enqueueSnackbar(t("languageSaveError"), { variant: "error" });
      return;
    }
    const queryKey = preferredLanguageQueryKey(sub);
    // An in-flight load read would otherwise overwrite the saved value.
    await queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData(queryKey, next);
    navigate(next);
  };

  return {
    switchLocale,
    isPending: isSaving || isNavigating,
    disabled: isImpersonating || attributesPending || isSaving || isNavigating,
  };
};
